import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Users, Target, CheckCircle, ArrowRight, Calendar, Star } from 'lucide-react';
import { useSkillFrameworksData } from '@/hooks/useConversationData';
import { useConversationGeneration, ConversationConfig } from '@/hooks/useConversationGeneration';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AppLayout } from '@/components/layout/AppLayout';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';

const ConversationConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const { toolId } = useParams<{ toolId: string }>();
  const { lang } = useParams<{ lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const { toast } = useToast();
  const [tool, setTool] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const [config, setConfig] = useState<ConversationConfig>({
    toolId: toolId || '',
    organizationSize: '',
    industry: '',
    templateType: 'performance_review',
    durationMonths: 12,
    includeSkillsAssessment: true,
    includeObjectives: true,
    checkInFrequency: 'monthly',
    additionalContext: '',
    selectedStageTypes: ['review', 'checkin', 'objective_setting'],
    selectedSkillFrameworks: []
  });

  const { data: skillFrameworks = [], isLoading: skillFrameworksLoading } = useSkillFrameworksData();
  const conversationGeneration = useConversationGeneration();

  useEffect(() => {
    const fetchTool = async () => {
      if (!toolId) return;
      
      try {
        const { data, error } = await supabase
          .from('tools')
          .select('id, name, description, icon')
          .eq('id', toolId)
          .eq('is_active', true)
          .single();

        if (error) throw error;
        setTool(data);
        setConfig(prev => ({ ...prev, toolId }));
      } catch (error) {
        console.error('Error fetching tool:', error);
        navigate(prefix);
      } finally {
        setLoading(false);
      }
    };

    fetchTool();
  }, [toolId, navigate]);

  const handleSkillFrameworkChange = (frameworkId: string, checked: boolean) => {
    setConfig(prev => ({
      ...prev,
      selectedSkillFrameworks: checked 
        ? [...prev.selectedSkillFrameworks, frameworkId]
        : prev.selectedSkillFrameworks.filter(id => id !== frameworkId)
    }));
  };

  const handleStageTypeChange = (stageType: string, checked: boolean) => {
    setConfig(prev => ({
      ...prev,
      selectedStageTypes: checked 
        ? [...prev.selectedStageTypes, stageType]
        : prev.selectedStageTypes.filter(t => t !== stageType)
    }));
  };

  const isConfigValid = () => {
    return config.organizationSize && config.industry && config.templateType;
  };

  const getConfigProgress = () => {
    const required = ['organizationSize', 'industry', 'templateType'];
    const optional = ['includeSkillsAssessment', 'includeObjectives', 'selectedStageTypes'];
    const completed = required.filter(field => config[field as keyof typeof config]).length +
                     (config.selectedStageTypes.length > 0 ? 1 : 0) +
                     (config.includeSkillsAssessment ? 1 : 0) +
                     (config.includeObjectives ? 1 : 0);
    return (completed / (required.length + optional.length)) * 100;
  };

  const handleGenerate = async () => {
    if (!isConfigValid()) return;

    setGenerating(true);
    try {
      const result = await conversationGeneration.mutateAsync(config);
      toast({
        title: "Template Generated!",
        description: "Your conversation template has been created successfully.",
      });
      const { lang } = useParams<{ lang?: string }>();
      const prefix = `/${lang || 'en'}`;
      navigate(`${prefix}/tools/${toolId}/results?templateId=${result.template.id}`);
    } catch (error) {
      console.error('Error generating template:', error);
      toast({
        title: "Generation Failed",
        description: "There was an error generating your template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin mx-auto w-8 h-8 border-2 border-primary border-t-transparent rounded-full mb-4"></div>
          <p className="text-muted-foreground">Loading tool configuration...</p>
        </div>
      </div>
    );
  }

  if (!tool) {
    return null;
  }

  return (
    <AppLayout>
      <AppHeader
        title={tool?.name || 'Conversation Tool'}
        subtitle="Create structured conversation templates for performance reviews, onboarding, and development discussions. Build meaningful dialogue frameworks with objectives, skill assessments, and regular check-ins."
        breadcrumbs={[
          { label: tool?.name || 'Tool', href: `/${(useParams<{ lang?: string }>().lang || 'en')}/tools/${toolId}` },
          { label: 'Configuration' }
        ]}
      />

      {/* Configuration Form */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
        <Card className="shadow-elegant bg-white/95 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Conversation Template Configuration
            </CardTitle>
            {/* Progress Indicator */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Configuration Progress</span>
                <span className="font-medium">{Math.round(getConfigProgress())}%</span>
              </div>
              <Progress value={getConfigProgress()} className="h-2" />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="organization-size">Organization Size</Label>
                  <Select value={config.organizationSize} onValueChange={(value) => setConfig(prev => ({ ...prev, organizationSize: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="startup">Startup (1-50)</SelectItem>
                      <SelectItem value="small">Small (51-200)</SelectItem>
                      <SelectItem value="medium">Medium (201-1000)</SelectItem>
                      <SelectItem value="large">Large (1000+)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select value={config.industry} onValueChange={(value) => setConfig(prev => ({ ...prev, industry: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="consulting">Consulting</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Type</Label>
                  <Select value={config.templateType} onValueChange={(value) => setConfig(prev => ({ ...prev, templateType: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select template type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="performance_review">Performance Review</SelectItem>
                      <SelectItem value="onboarding">Onboarding Check-In</SelectItem>
                      <SelectItem value="career_development">Career Development</SelectItem>
                      <SelectItem value="wellbeing">Wellbeing & Support</SelectItem>
                      <SelectItem value="exit_interview">Exit Interview</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Duration (Months)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="24"
                    value={config.durationMonths}
                    onChange={(e) => setConfig(prev => ({ ...prev, durationMonths: parseInt(e.target.value) || 12 }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Check-in Frequency</Label>
                <Select value={config.checkInFrequency} onValueChange={(value) => setConfig(prev => ({ ...prev, checkInFrequency: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="How often?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Template Features */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Template Features</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-skills"
                    checked={config.includeSkillsAssessment}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, includeSkillsAssessment: checked as boolean }))}
                  />
                  <Label htmlFor="include-skills" className="cursor-pointer">
                    Include Skills Assessment
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-objectives"
                    checked={config.includeObjectives}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, includeObjectives: checked as boolean }))}
                  />
                  <Label htmlFor="include-objectives" className="cursor-pointer">
                    Include Objectives & Goal Setting
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Stage Types */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Conversation Stages</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'review', label: 'Performance Review', icon: Star },
                  { value: 'checkin', label: 'Regular Check-in', icon: Calendar },
                  { value: 'objective_setting', label: 'Goal Setting', icon: Target },
                  { value: 'skills_assessment', label: 'Skills Assessment', icon: Users }
                ].map((stage) => (
                  <div key={stage.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={stage.value}
                      checked={config.selectedStageTypes.includes(stage.value)}
                      onCheckedChange={(checked) => handleStageTypeChange(stage.value, checked as boolean)}
                    />
                    <Label htmlFor={stage.value} className="text-sm cursor-pointer flex items-center gap-2">
                      <stage.icon className="w-4 h-4" />
                      {stage.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Skill Frameworks */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Skill Frameworks (Optional)</Label>
              {skillFrameworksLoading ? (
                <div className="text-sm text-muted-foreground">Loading skill frameworks...</div>
              ) : (
                <>
                  <div className="space-y-2">
                    {skillFrameworks.map((framework) => (
                      <div key={framework.id} className="flex items-start space-x-2 p-2 border rounded-lg">
                        <Checkbox
                          id={framework.id}
                          checked={config.selectedSkillFrameworks.includes(framework.id)}
                          onCheckedChange={(checked) => handleSkillFrameworkChange(framework.id, checked as boolean)}
                        />
                        <div className="flex-1">
                          <Label htmlFor={framework.id} className="cursor-pointer font-medium">
                            {framework.name}
                          </Label>
                          <p className="text-sm text-muted-foreground">{framework.description}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {framework.categories.map(category => (
                              <Badge key={category.id} variant="outline" className="text-xs">
                                {category.name} ({category.skills.length})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="context">Additional Context (Optional)</Label>
              <Textarea
                id="context"
                placeholder="Any specific goals, company values, or particular focus areas for these conversations..."
                value={config.additionalContext}
                onChange={(e) => setConfig(prev => ({ ...prev, additionalContext: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="pt-4">
              <Button 
                onClick={handleGenerate}
                disabled={!isConfigValid() || generating}
                className="w-full"
                variant="gradient"
                size="lg"
              >
                <div className="flex items-center gap-2">
                  {generating ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Users className="w-4 h-4" />
                  )}
                  {generating ? 'Generating Template...' : 'Generate Conversation Template'}
                  {!generating && <ArrowRight className="w-4 h-4" />}
                </div>
              </Button>
            </div>

            {/* Required fields indicator */}
            {!isConfigValid() && (
              <div className="text-center text-sm text-muted-foreground">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  Please complete all required fields to continue
                </div>
                <div className="text-xs">
                  Required: Organization Size, Industry, Template Type
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
      
      <Footer />
    </AppLayout>
  );
};

export default ConversationConfiguration;